import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Sun, Moon, Monitor } from 'lucide-react';

const AgencySettings = () => {
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();

  return (
    <div className="space-y-6 max-w-2xl" data-testid="agency-settings">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Settings</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1">Manage your agency preferences</p>
      </div>

      {/* Profile */}
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle>Profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#556B2F] to-[#6B8E23] flex items-center justify-center text-white font-semibold text-2xl">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <h3 className="font-medium text-slate-900 dark:text-white text-lg">{user?.name}</h3>
              <p className="text-slate-500">{user?.email}</p>
              <p className="text-sm text-slate-400 capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Appearance */}
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle>Appearance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Button
              variant={theme === 'light' ? 'default' : 'outline'}
              onClick={() => setTheme('light')}
              className={theme === 'light' ? 'bg-[#556B2F] text-white' : ''}
              data-testid="theme-light"
            >
              <Sun className="w-4 h-4 mr-2" />
              Light
            </Button>
            <Button
              variant={theme === 'dark' ? 'default' : 'outline'}
              onClick={() => setTheme('dark')}
              className={theme === 'dark' ? 'bg-[#556B2F] text-white' : ''}
              data-testid="theme-dark"
            >
              <Moon className="w-4 h-4 mr-2" />
              Dark
            </Button>
            <Button
              variant={theme === 'system' ? 'default' : 'outline'}
              onClick={() => setTheme('system')}
              className={theme === 'system' ? 'bg-[#556B2F] text-white' : ''}
              data-testid="theme-system"
            >
              <Monitor className="w-4 h-4 mr-2" />
              System
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardHeader>
          <CardTitle>Notifications</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Email Notifications</Label>
              <p className="text-sm text-slate-500">Receive updates via email</p>
            </div>
            <Switch defaultChecked data-testid="email-notifications-switch" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label>Post Approvals</Label>
              <p className="text-sm text-slate-500">Get notified when clients approve posts</p>
            </div>
            <Switch defaultChecked data-testid="approval-notifications-switch" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label>Change Requests</Label>
              <p className="text-sm text-slate-500">Get notified when clients request changes</p>
            </div>
            <Switch defaultChecked data-testid="changes-notifications-switch" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AgencySettings;
